import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import * as ep from '../api/endpoints';
import type { CreditRow } from '../api/types';
import { useAuth } from '../app/auth';
import { PushToggle } from '../components/PushToggle';
import { Empty, ErrorBox, Loading, Money } from '../components/ui';
import { formatDate, todayTR } from '../lib/dates';

/** Kalan güne göre etiket: gecikti / bugün / N gün kaldı. */
export function dueLabel(days: number | null): [string, string] {
  if (days === null) return ['—', ''];
  if (days < 0) return [`${-days} gün gecikti`, 'bad'];
  if (days === 0) return ['Bugün', 'bad'];
  if (days <= 3) return [`${days} gün kaldı`, 'warn'];
  if (days <= 7) return [`${days} gün kaldı`, 'info'];
  return [`${days} gün kaldı`, ''];
}

/**
 * Vadeli (veresiye) ödemeler: ödeme tarihi yaklaşanlar üstte. Vadeden 1 hafta, 3 gün önce ve vade günü
 * şirketteki herkese bildirim gider. Ödendiğinde fiş sahibi ya da yönetici "Ödendi" olarak işaretler.
 */
export function PaymentsScreen() {
  const [tab, setTab] = useState<'open' | 'paid'>('open');
  const q = useQuery({ queryKey: ['credit', tab], queryFn: () => ep.creditPayments(tab) });
  const rows = q.data ?? [];
  const totalOpen = rows.reduce((t, r) => t + Math.round(Number(r.net_contribution ?? r.amount) * 100), 0);
  return (
    <div className="stack">
      <div className="page-head"><h1>Vadeli ödemeler</h1></div>
      <p className="small muted" style={{ margin: 0 }}>
        Veresiye alınan fişler. Ödeme gününden <strong>1 hafta önce</strong>, <strong>3 gün önce</strong> ve <strong>ödeme günü</strong>
        herkese bildirim gider.
      </p>
      <PushToggle compact />
      <div className="tabs" role="tablist">
        <button role="tab" aria-selected={tab === 'open'} className={tab === 'open' ? 'active' : ''} onClick={() => setTab('open')}>Ödenecekler</button>
        <button role="tab" aria-selected={tab === 'paid'} className={tab === 'paid' ? 'active' : ''} onClick={() => setTab('paid')}>Ödenenler</button>
      </div>
      <ErrorBox error={q.error} />
      {!q.data ? <Loading /> : !rows.length ? (
        <Empty>{tab === 'open' ? 'Ödenmeyi bekleyen veresiye yok.' : 'Henüz ödendi olarak işaretlenen veresiye yok.'}</Empty>
      ) : (
        <>
          {tab === 'open' && (
            <div className="card stat"><div className="label">Toplam ödenecek</div><div className="value"><Money value={(totalOpen / 100).toFixed(2)} /></div></div>
          )}
          <div className="card"><ul className="list">
            {rows.map((r) => <CreditItem key={r.id} r={r} />)}
          </ul></div>
        </>
      )}
    </div>
  );
}

function CreditItem({ r }: { r: CreditRow }) {
  const { me, isAdmin } = useAuth();
  const qc = useQueryClient();
  const [asking, setAsking] = useState(false);
  const [paidOn, setPaidOn] = useState(todayTR());
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ['credit'] }), qc.invalidateQueries({ queryKey: ['receipt', r.id] })]);
  const settle = useMutation({ mutationFn: () => ep.settleCredit(r.id, paidOn), onSuccess: refresh });
  const undo = useMutation({ mutationFn: () => ep.unsettleCredit(r.id), onSuccess: refresh });
  const can = me?.user.id === r.owner_id || isAdmin;
  const [label, cls] = dueLabel(r.days_left);
  return (
    <li>
      <div className="item" style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Link to={`/fis/${r.id}`} style={{ display: 'block', flex: 1, minWidth: 0, padding: 0 }}>
          <div className="title">{r.vendor_name ?? 'Satıcı yok'} · {r.category}</div>
          <div className="small muted">{r.description}</div>
          <div className="small muted">Kaydeden: {r.owner_name} · Fiş tarihi {formatDate(r.document_date)}</div>
          <div className="small" style={{ marginTop: 4 }}>
            Son ödeme: <strong>{formatDate(r.payment_due_date)}</strong>{' '}
            {r.settlement ? <span className="badge ok">Ödendi · {formatDate(r.settlement.paid_on)}</span>
              : <span className={`badge ${cls}`}>{label}</span>}
          </div>
        </Link>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 700 }}><Money value={r.amount} /></div>
          {can && !r.settlement && !asking && <button className="btn small primary" style={{ marginTop: 6 }} onClick={() => setAsking(true)}>Ödendi</button>}
          {can && r.settlement && (
            <button className="btn small" style={{ marginTop: 6 }} disabled={undo.isPending}
              onClick={() => { if (confirm('Ödeme kaydı geri alınsın mı?')) undo.mutate(); }}>Geri al</button>
          )}
        </div>
      </div>
      {asking && (
        <div className="row" style={{ padding: '0 16px 12px' }}>
          <input type="date" value={paidOn} max={todayTR()} onChange={(e) => setPaidOn(e.target.value)} aria-label="Ödeme tarihi" style={{ maxWidth: 170 }} />
          <button className="btn small primary" disabled={settle.isPending || !paidOn} onClick={() => settle.mutate()}>Kaydet</button>
          <button className="btn small" onClick={() => setAsking(false)}>Vazgeç</button>
        </div>
      )}
      {(settle.error || undo.error) && <div style={{ padding: '0 16px 12px' }}><ErrorBox error={settle.error ?? undo.error} /></div>}
    </li>
  );
}
